import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import api from '../lib/api';
import { ApiResponse } from '../types/api';
import type { UnsubscribeStatus } from '../types/emailUnsubscribe';

// The unsubscribe link needs no session. The token travels as a query param
// (not baked into the URL string) so the axios error logging, which prints
// `config.url`, never echoes it.

export const emailUnsubscribeKeys = {
  status: (token: string) => ['email', 'unsubscribe', token] as const,
};

/**
 * Look up what an unsubscribe link points at. Read-only: a GET never
 * unsubscribes, so link scanners that prefetch emails can't do it either.
 * GET /api/v1/email/unsubscribe?token=
 */
export const fetchUnsubscribeStatus = async (token: string): Promise<UnsubscribeStatus> => {
  const response = await api.get<ApiResponse<UnsubscribeStatus>>('/email/unsubscribe', {
    params: { token },
  });
  return response.data.data!;
};

export const useUnsubscribeStatus = (token: string): UseQueryResult<UnsubscribeStatus, Error> => {
  return useQuery({
    queryKey: emailUnsubscribeKeys.status(token),
    queryFn: () => fetchUnsubscribeStatus(token),
    enabled: token.length > 0,
    // The page offers its own "Try again"; a 400 (bad link) never heals on retry.
    retry: false,
  });
};

/**
 * Withdraw marketing consent for the link's address. Idempotent.
 * POST /api/v1/email/unsubscribe?token=
 */
export const unsubscribeFromMarketing = async (token: string): Promise<void> => {
  await api.post('/email/unsubscribe', {}, { params: { token } });
};

export const useUnsubscribeFromMarketing = (token: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => unsubscribeFromMarketing(token),
    retry: false,
    onSuccess: () => {
      queryClient.setQueryData<UnsubscribeStatus>(emailUnsubscribeKeys.status(token), (prev) =>
        prev ? { ...prev, already_unsubscribed: true } : prev
      );
    },
  });
};
